import type React from "react";
import { resolverIdioma, aplicarIdiomaResolvido } from "./i18n/languageCascade";
import { normalizarId } from "./hooks/useScope";
import { Routes, Route, Navigate, BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Dashboard, { DashboardCards } from "./layouts/Dashboard";
import { LoadingProvider } from "./components/loadingProvider";
import { useEffect, createContext, useContext, useState, useCallback, useRef, useMemo } from "react";
import CryptoJS from 'crypto-js';
import TenantTypeList from "./pages/tenantTypes/TenantTypeList";
import TenantTypeForm from "./pages/tenantTypes/TenantTypeForm";
import PartnerList from "./pages/partners/PartnerList";
import PartnerForm from "./pages/partners/PartnerForm";
import TenantList from "./pages/tenants/TenantList";
import TenantForm from "./pages/tenants/TenantForm";
import UserList from "./pages/users/UserList";
import UserForm from "./pages/users/UserForm";
import RoleList from "./pages/roles/RoleList";
import RoleForm from "./pages/roles/RoleForm";
import StableList from "./pages/stables/StableList";
import StableForm from "./pages/stables/StableForm";
import StallList from "./pages/stalls/StallList";
import StallForm from "./pages/stalls/StallForm";
import HorseBreedList from "./pages/horseBreeds/HorseBreedList";
import HorseBreedForm from "./pages/horseBreeds/HorseBreedForm";
import HorseList from "./pages/horses/HorseList";
import HorseForm from "./pages/horses/HorseForm";
import RecorderList from "./pages/recorders/RecorderList";
import RecorderForm from "./pages/recorders/RecorderForm";
import CameraList from "./pages/cameras/CameraList";
import CameraForm from "./pages/cameras/CameraForm";
import StreamViewer from "./components/StreamViewer";
import DevAlert from "./components/DevAlert";
import { getTheme, type AppColorMode } from "./components/theme";
import { ColorModeContext, COLOR_MODE_KEY, useColorMode } from "./theme/colorMode";
import NotFoundPage from "./pages/notFound/page";
import { API_URL } from "./config";
import api from "./services/api";

// Chave secreta para criptografia (em produção, use variável de ambiente)
const SECRET_KEY = 'Hors&T&chN0';

// Interface para o estado da navegação
interface NavigationState {
  page: string;
  params: Record<string, any>;
  timestamp: number;
}

// ✅ NOVO: Mapeamento de páginas internas para URLs públicas
const PAGE_URL_MAPPING : Record<string, string> = {
  'dashboard': '/',
  'tipos': '/tipos',
  'partners': '/partners',
  'proprietarios': '/proprietarios', 
  'usuarios': '/usuarios',
  'permissoes': '/permissoes',
  'estabulos': '/estabulos',
  'baias': '/baias',
  'racas': '/racas',
  'cavalos': '/cavalos',
  'gravadores': '/gravadores',
  'cameras': '/cameras',
  'transmissao': '/transmissao'
} as const;

// ✅ NOVO: Mapeamento reverso de URLs para páginas internas
const URL_PAGE_MAPPING: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_URL_MAPPING).map(([page, url]) => [url, page])
);

// ✅ NOVO: URLs válidas da aplicação
const VALID_URLS = Object.values(PAGE_URL_MAPPING);

// Utilitários de criptografia
const CryptoUtils = {
  encrypt: (data: NavigationState): string => {
    const jsonString = JSON.stringify(data);
    return CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
  },

  decrypt: (encryptedData: string): NavigationState | null => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      const parsed = JSON.parse(decryptedData);

      // Verificar se o estado não está muito antigo (24 horas)
      const maxAge = 24 * 60 * 60 * 1000; // 24 horas em ms
      const age = Date.now() - (parsed.timestamp || 0);

      if (age > maxAge) {
        console.log('🕰️ Estado criptografado expirado:', age / 1000 / 3600, 'horas');
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('❌ Erro ao descriptografar:', error);
      return null;
    }
  }
};

// ✅ UTILITÁRIO MELHORADO - Verificação mais robusta de token
const TokenUtils = {
  isTokenValid: () => {
    const token = localStorage.getItem("token");

    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  },

  getToken: () => localStorage.getItem("token"),

  clearTokens: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("userId");
  },

  // ✅ ADICIONAR ESTA FUNÇÃO
  tryRefreshToken: async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/user/rtoken`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();

      if (!data.token) {
        return false;
      }

      localStorage.setItem("token", data.token);

      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }

      return true;
    } catch (error) {
      console.error("Erro ao tentar renovar token:", error);
      return false;
    }
  },
};

// ✅ NOVO: Utilitários para gerenciamento de URL
const URLUtils = {
  // Converte página interna para URL pública
  pageToUrl: (page: string): string => {
  return PAGE_URL_MAPPING[page] || '/';
},

  // Converte URL pública para página interna
  urlToPage: (url: string): string => {
    // Remover query parameters e hash
    const cleanUrl = url.split('?')[0].split('#')[0];
    return URL_PAGE_MAPPING[cleanUrl] || 'dashboard';
  },

  // Verifica se a URL é válida
  isValidUrl: (url: string): boolean => {
    const cleanUrl = url.split('?')[0].split('#')[0];
    return VALID_URLS.includes(cleanUrl);
  },

  // Atualiza a URL do navegador sem recarregar
  updateBrowserUrl: (page: string, replace: boolean = false): void => {
    const url = URLUtils.pageToUrl(page);
    const method = replace ? 'replaceState' : 'pushState';
    
    if (window.location.pathname !== url) {
      window.history[method]({}, '', url);
      console.log(`🔗 URL atualizada (${method}):`, url);
    }
  },

  // Obtém a página atual da URL do navegador
  getCurrentPageFromUrl: (): string => {
    return URLUtils.urlToPage(window.location.pathname);
  }
};

// Páginas válidas da aplicação (mantido para compatibilidade)
const VALID_PAGES = Object.keys(PAGE_URL_MAPPING);

// Context para gerenciar navegação criptografada
interface CryptoNavigationContextType {
  currentPage: string;
  params: Record<string, any>;
  navigateToPage: (page: string, params?: Record<string, any>) => void;
  getParam: (key: string) => string | undefined;
  clearNavigation: () => void;
  isInitialized: boolean;
  getCurrentShareableUrl: () => string;
  forceLogout: () => void;
}

const CryptoNavigationContext = createContext<CryptoNavigationContextType>({
  currentPage: 'login',
  params: {},
  navigateToPage: () => {},
  getParam: () => undefined,
  clearNavigation: () => {},
  isInitialized: false,
  getCurrentShareableUrl: () => '',
  forceLogout: () => {}
});

export const useCryptoNavigation = () => {
  const context = useContext(CryptoNavigationContext);
  if (!context) {
    throw new Error('useCryptoNavigation deve ser usado dentro de um CryptoNavigationProvider');
  }
  return context;
};

// Provider para navegação criptografada
const CryptoNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentPage, setCurrentPage] = useState<string>('login');
  const [params, setParams] = useState<Record<string, any>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, logout } = useAuth();
  
  // ✅ PREVENIR LOOPS: Usar refs para controlar quando fazer verificações
  const lastAuthCheck = useRef<number>(0);
  const isProcessingAuth = useRef<boolean>(false);

  // Função para forçar logout e limpeza completa
  const forceLogout = useCallback(() => {
    console.log('🚨 Forçando logout completo...');

    // Prevenir chamadas múltiplas
    if (isProcessingAuth.current) {
      console.log('⏸️ Logout já em andamento, ignorando...');
      return;
    }

    isProcessingAuth.current = true;

    // Limpar todos os tokens e dados
    TokenUtils.clearTokens();

    // Chamar logout do contexto de auth
    logout();

    // Limpar estado local mas MANTER isInitialized como true
    setCurrentPage('login');
    setParams({});

    // ✅ NOVO: Ir para página de login
    URLUtils.updateBrowserUrl('login', true);

    console.log('✅ Logout forçado concluído');
    
    // Permitir nova verificação após um tempo
    setTimeout(() => {
      isProcessingAuth.current = false;
    }, 1000);
  }, [logout]);

  // Função para limpar navegação
  const clearNavigation = useCallback(() => {
    console.log('🧹 Limpando navegação criptografada');
    setCurrentPage('login');
    setParams({});
    localStorage.removeItem('n0-encrypted-state');
    localStorage.removeItem('n0-shareable-url');

    // ✅ NOVO: Voltar para home
    URLUtils.updateBrowserUrl('dashboard', true);
  }, []);

  // Função para validar se página é válida
  const isValidPage = useCallback((page: string): boolean => {
    return VALID_PAGES.includes(page);
  }, []);

  // ✅ MODIFICADO: Função para gerar URL compartilhável com parâmetros criptografados
  const getCurrentShareableUrl = useCallback((): string => {
    if (currentPage === 'login' || currentPage === 'dashboard') {
      return `${window.location.origin}${URLUtils.pageToUrl(currentPage)}`;
    }

    const state: NavigationState = {
      page: currentPage,
      params: params,
      timestamp: Date.now()
    };

    const encryptedState = CryptoUtils.encrypt(state);
    const currentUrl = URLUtils.pageToUrl(currentPage);
    return `${window.location.origin}${currentUrl}?s=${encodeURIComponent(encryptedState)}`;
  }, [currentPage, params]);

  // ✅ MODIFICADO: Função para salvar estado atual
const saveCurrentState = useCallback(() => {
  const hasValidToken = TokenUtils.isTokenValid();
  const userIsAuthenticated = isAuthenticated || hasValidToken;

  if (currentPage !== 'login' && userIsAuthenticated) {
    // ✅ VALIDAÇÃO: Não salvar se tenantId for 1
    const tenantId = params?.tenantId;
    if (tenantId === '1' || tenantId === 1) {
      console.warn('⚠️ Tentativa de salvar tenantId inválido (1), ignorando...');
      return;
    }
    
    const state: NavigationState = {
      page: currentPage,
      params: params,
      timestamp: Date.now()
    };

    const encryptedState = CryptoUtils.encrypt(state);
    localStorage.setItem('n0-encrypted-state', encryptedState);

    // ✅ NOVO: Atualizar URL do navegador
    URLUtils.updateBrowserUrl(currentPage);

    console.log('💾 Estado salvo:', { page: currentPage, params, url: URLUtils.pageToUrl(currentPage) });
  }
}, [currentPage, params, isAuthenticated]);

  // ✅ NOVO: Limpar tenantId inválido do localStorage na inicialização
useEffect(() => {
  const savedState = localStorage.getItem('n0-encrypted-state');
  if (savedState) {
    try {
      const decryptedState = CryptoUtils.decrypt(savedState);
      if (decryptedState?.params?.tenantId === '1' || decryptedState?.params?.tenantId === 1) {
        console.warn('⚠️ Detectado tenantId inválido (1) no localStorage, limpando...');
        localStorage.removeItem('n0-encrypted-state');
        localStorage.removeItem('n0-shareable-url');
      }
    } catch (e) {
      console.error('Erro ao validar estado salvo:', e);
    }
  }
}, []); // Executar apenas uma vez na montagem

// ✅ MODIFICADO: INICIALIZAÇÃO MELHORADA - Considerar URL atual
useEffect(() => {
  // ... resto do código existente
    // Aguardar AuthContext terminar de carregar
    if (isLoading) {
      console.log('⏳ Aguardando AuthContext terminar de carregar...');
      return;
    }

    if (!isInitialized) {
      console.log('🚀 Inicializando navegação...');

      const hasValidToken = TokenUtils.isTokenValid();
      const currentUrlPage = URLUtils.getCurrentPageFromUrl();
      
      console.log('🔍 Inicialização:', {
        hasValidToken,
        isAuthenticated,
        currentUrl: window.location.pathname,
        currentUrlPage
      });

      // Decidir página inicial baseado no token E autenticação
      if (hasValidToken && isAuthenticated) {
        // ✅ NOVO: Verificar se há parâmetros criptografados na URL
        const urlParams = new URLSearchParams(window.location.search);
        const encryptedState = urlParams.get('s');
        
        if (encryptedState) {
          // Há estado criptografado na URL
          const decryptedState = CryptoUtils.decrypt(encryptedState);
          if (decryptedState && isValidPage(decryptedState.page)) {
            console.log('✅ Estado restaurado da URL:', decryptedState);
            setCurrentPage(decryptedState.page);
            setParams(decryptedState.params);
            
            // Limpar parâmetros da URL, mas manter o path
            URLUtils.updateBrowserUrl(decryptedState.page, true);
          } else {
            // Estado inválido, usar página da URL ou dashboard
            const pageToUse = URLUtils.isValidUrl(window.location.pathname) ? currentUrlPage : 'dashboard';
            setCurrentPage(pageToUse);
            setParams({});
            URLUtils.updateBrowserUrl(pageToUse, true);
          }
        } else {
          // ✅ NOVO: Não há estado criptografado, verificar se URL é válida
          if (URLUtils.isValidUrl(window.location.pathname)) {
            // URL válida, usar página da URL
            setCurrentPage(currentUrlPage);
            setParams({});
            
            // Tentar recuperar parâmetros do localStorage
            // Tentar recuperar parâmetros do localStorage
const savedState = localStorage.getItem('n0-encrypted-state');
if (savedState) {
  const decryptedState = CryptoUtils.decrypt(savedState);
  if (decryptedState && decryptedState.page === currentUrlPage) {
    // ✅ VALIDAÇÃO: Verificar se tenantId salvo ainda é válido
    const savedTenantId = decryptedState.params?.tenantId;
    
    // Buscar usuário logado do localStorage
    const userStr = localStorage.getItem('user');
    let userTenantId = null;
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userTenantId = normalizarId(user?.tenantId, user?.tenant_id);
      } catch (e) {
        console.error('Erro ao parsear usuário:', e);
      }
    }
    
    console.log('🔍 Validando tenantId:', { 
      savedTenantId, 
      userTenantId,
      willRestore: !savedTenantId || !userTenantId || savedTenantId === userTenantId 
    });
    
    // Só restaurar params se tenantId for compatível
    if (!savedTenantId || userTenantId === null || String(savedTenantId) === String(userTenantId)) {
      console.log('✅ Parâmetros restaurados do localStorage para página:', currentUrlPage);
      setParams(decryptedState.params);
    } else {
      console.log('⚠️ TenantId salvo não corresponde ao usuário, ignorando params salvos');
      setParams({});
    }
  }
}
          } else {
            // URL inválida, tentar recuperar estado salvo
            const savedState = localStorage.getItem('n0-encrypted-state');
            if (savedState) {
              const decryptedState = CryptoUtils.decrypt(savedState);
              if (decryptedState && isValidPage(decryptedState.page)) {
                setCurrentPage(decryptedState.page);
                setParams(decryptedState.params);
                URLUtils.updateBrowserUrl(decryptedState.page, true);
              } else {
                setCurrentPage('dashboard');
                setParams({});
                URLUtils.updateBrowserUrl('dashboard', true);
              }
            } else {
              setCurrentPage('dashboard');
              setParams({});
              URLUtils.updateBrowserUrl('dashboard', true);
            }
          }
        }
      } else {
        // Sempre garantir que vai para login se não está autenticado
        console.log('🔐 Token inválido ou não autenticado, indo para login');
        setCurrentPage('login');
        setParams({});
        URLUtils.updateBrowserUrl('dashboard', true);
      }

      setIsInitialized(true);
    }
  }, [isInitialized, isValidPage, isAuthenticated, isLoading]);

  // ✅ MONITORAMENTO DE AUTENTICAÇÃO OTIMIZADO - Prevenir loops
  useEffect(() => {
    if (!isInitialized || isProcessingAuth.current) return;

    const now = Date.now();
    
    // ✅ THROTTLING: Só verificar a cada 5 segundos para evitar loops
    if (now - lastAuthCheck.current < 5000) {
      return;
    }
    
    lastAuthCheck.current = now;

    const hasValidToken = TokenUtils.isTokenValid();

    console.log('🔄 Verificação de auth (throttled):', {
      hasValidToken,
      isAuthenticated,
      currentPage,
      timestamp: new Date().toISOString()
    });

    // ✅ CONDIÇÃO MAIS RESTRITA: Só forçar logout se realmente necessário
    const hasRefreshToken = !!localStorage.getItem('refreshToken');

if (!isAuthenticated && !hasRefreshToken && currentPage !== 'login') {
      console.log('🔐 Forçando logout - token inválido E não autenticado');
      
      isProcessingAuth.current = true;
      setCurrentPage('login');
      setParams({});
      clearNavigation();
      
      setTimeout(() => {
        isProcessingAuth.current = false;
      }, 1000);
      
      return;
    }

    // ✅ NAVEGAÇÃO PARA DASHBOARD: Só se ambos os estados confirmarem autenticação
    if (hasValidToken && isAuthenticated && currentPage === 'login') {
      console.log('✅ Usuário autenticado, redirecionando para dashboard');
      setCurrentPage('dashboard');
      setParams({});
      URLUtils.updateBrowserUrl('dashboard');
    }
  }, [isAuthenticated, currentPage, isInitialized, clearNavigation, isLoading]);

  // Salvar estado sempre que mudar
  useEffect(() => {
    if (isInitialized) {
      saveCurrentState();
    }
  }, [currentPage, params, isInitialized, saveCurrentState]);

  // ✅ FUNÇÃO MELHORADA - Permitir navegação e atualizar URL
const navigateToPage = useCallback((page: string, newParams: Record<string, any> = {}) => {
  console.log(`🧭 navigateToPage: ${page}`, { newParams, currentParams: params });

  // ✅ VALIDAÇÃO: Se tenantId for 1, tentar buscar do usuário
  if (newParams.tenantId === '1' || newParams.tenantId === 1) {
    console.warn('⚠️ TenantId 1 detectado na navegação, tentando corrigir...');
    
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const userTenantId = normalizarId(user?.tenantId, user?.tenant_id);
        if (userTenantId && userTenantId !== 1) {
          newParams.tenantId = String(userTenantId);
          console.log('✅ TenantId corrigido para:', userTenantId);
        } else {
          // Se usuário também tem tenantId 1 ou não tem, remover o parâmetro
          delete newParams.tenantId;
          console.log('⚠️ Usuário sem tenantId válido, removendo parâmetro');
        }
      }
    } catch (e) {
      console.error('Erro ao corrigir tenantId:', e);
      delete newParams.tenantId;
    }
  }

  // ✅ CORREÇÃO DO BUG: Verificar se página E parâmetros são iguais
  if (currentPage === page && JSON.stringify(params) === JSON.stringify(newParams)) {
    console.log('⏭️ Página e parâmetros idênticos, pulando navegação');
    return;
  }

    // ✅ NOVO LOG: Mostrar por que está navegando
    if (currentPage === page) {
      console.log('🔄 Mesma página, mas parâmetros diferentes - prosseguindo com navegação');
    } else {
      console.log('🚀 Página diferente - prosseguindo com navegação');
    }

    // Verificar se a página é válida
    if (!isValidPage(page)) {
      page = 'dashboard';
      newParams = {};
    }

    // Atualizar estado local
    setCurrentPage(page);
    setParams(newParams);

    // ✅ NOVO: Atualizar URL do navegador
    URLUtils.updateBrowserUrl(page);
    
    console.log('✅ Navegação concluída:', { 
      page, 
      params: newParams, 
      url: URLUtils.pageToUrl(page) 
    });
  }, [currentPage, params, isValidPage]);

  const getParam = useCallback((key: string) => params[key], [params]);

  // ✅ NOVO: Handler para mudanças na URL do navegador
  useEffect(() => {
  const handlePopState = async () => {
    console.log("🔙 Detectado navegação do histórico do navegador");

    const hasValidToken = TokenUtils.isTokenValid();

    if (!hasValidToken) {
      console.log("🔄 Token inválido. Tentando renovar com refresh token...");

      const refreshed = await TokenUtils.tryRefreshToken();

      if (!refreshed) {
        console.log("🔐 Refresh token inválido ou expirado. Indo para login.");

        setCurrentPage("login");
        setParams({});
        URLUtils.updateBrowserUrl("login", true);
        return;
      }

      console.log("✅ Token renovado com sucesso. Continuando navegação.");
    }

    const currentUrlPage = URLUtils.getCurrentPageFromUrl();

    const urlParams = new URLSearchParams(window.location.search);
    const encryptedState = urlParams.get("s");

    if (encryptedState) {
      const decryptedState = CryptoUtils.decrypt(encryptedState);

      if (decryptedState && isValidPage(decryptedState.page)) {
        console.log("✅ Estado restaurado da URL no histórico:", decryptedState);

        setCurrentPage(decryptedState.page);
        setParams(decryptedState.params);

        URLUtils.updateBrowserUrl(decryptedState.page, true);
        return;
      }
    }

    if (URLUtils.isValidUrl(window.location.pathname)) {
      console.log("✅ Navegando para página da URL:", currentUrlPage);

      setCurrentPage(currentUrlPage);

      const savedState = localStorage.getItem("n0-encrypted-state");

      if (savedState) {
        const decryptedState = CryptoUtils.decrypt(savedState);

        if (decryptedState && decryptedState.page === currentUrlPage) {
          setParams(decryptedState.params);
        } else {
          setParams({});
        }
      } else {
        setParams({});
      }

      return;
    }

    console.log("🏠 Não foi possível restaurar estado, indo para dashboard");

    setCurrentPage("dashboard");
    setParams({});
    URLUtils.updateBrowserUrl("dashboard", true);
  };

  window.addEventListener("popstate", handlePopState);

  return () => {
    window.removeEventListener("popstate", handlePopState);
  };
}, [isValidPage]);

  // ✅ NOVO: Sincronizar com mudanças diretas na URL
  useEffect(() => {
    if (!isInitialized) return;

    const currentUrlPage = URLUtils.getCurrentPageFromUrl();
    
    // Se a página da URL for diferente da página atual, sincronizar
    if (currentUrlPage !== currentPage && URLUtils.isValidUrl(window.location.pathname)) {
      console.log('🔄 Sincronizando com mudança na URL:', {
        urlPage: currentUrlPage,
        currentPage: currentPage
      });
      
      setCurrentPage(currentUrlPage);
      // Manter parâmetros atuais ou limpar se não aplicáveis
      // Você pode implementar lógica mais sofisticada aqui
    }
  }, [location.pathname, currentPage, isInitialized]);

  console.log('🔄 CryptoNavigationProvider:', { 
    currentPage, 
    isInitialized, 
    isAuthenticated,
    currentUrl: window.location.pathname 
  });

  return (
    <CryptoNavigationContext.Provider value={{
      currentPage,
      params,
      navigateToPage,
      getParam,
      clearNavigation,
      isInitialized,
      getCurrentShareableUrl,
      forceLogout
    }}>
      {children}
    </CryptoNavigationContext.Provider>
  );
};

// Componente que renderiza a página atual - MELHORADO
const PageRenderer: React.FC = () => {
  const { currentPage, params, isInitialized } = useCryptoNavigation();
  const { isAuthenticated, isLoading } = useAuth();

  console.log('📄 PageRenderer:', {
    currentPage,
    isAuthenticated,
    isLoading,
    isInitialized
  });

  // ✅ Aguardar inicialização completa
  if (!isInitialized || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
      }}>
        Carregando aplicação...
      </div>
    );
  }

  // ✅ Determinar se deve mostrar login baseado no estado atual
  const hasValidToken = TokenUtils.isTokenValid();
  const shouldShowLogin = currentPage === 'login' || !isAuthenticated;

  console.log('🔍 PageRenderer shouldShowLogin:', shouldShowLogin, {
    currentPage,
    isAuthenticated,
    hasValidToken
  });

  // ✅ Se deve mostrar login, renderizar login
  if (shouldShowLogin) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Login />
      </div>
    );
  }

  const renderPage = () => {
    const mode = params.mode || 'list';

    console.log('🎨 renderPage:', { currentPage, mode, params });

    switch (currentPage) {
      case 'tipos':
        return mode === 'list' ? <TenantTypeList /> : <TenantTypeForm />;

      case 'partners':
        return mode === 'list' ? <PartnerList /> : <PartnerForm />;

      case 'proprietarios':
        return mode === 'list' ? <TenantList /> : <TenantForm />;

      case 'usuarios':
        return mode === 'list' ? <UserList /> : <UserForm />;

      case 'permissoes':
        return mode === 'list' ? <RoleList /> : <RoleForm />;

      case 'estabulos':
        return mode === 'list' ? <StableList /> : <StableForm />;

      case 'baias':
        return mode === 'list' ? <StallList /> : <StallForm />;

      case 'racas':
        return mode === 'list' ? <HorseBreedList /> : <HorseBreedForm />;

      case 'cavalos':
        return mode === 'list' ? <HorseList /> : <HorseForm />;

      case 'gravadores':
        return mode === 'list' ? <RecorderList /> : <RecorderForm />;

      case 'cameras':
        return mode === 'list' ? <CameraList /> : <CameraForm />;

      case 'transmissao':
        return <StreamViewer />;

      case 'dashboard':
        return <DashboardCards />;

      default:
        console.log('🚨 Página não reconhecida:', currentPage, '- indo para dashboard');
        return <DashboardCards />;
    }
  };

  return <>{renderPage()}</>;
};

// ✅ COMPONENTE PRINCIPAL DE ROTEAMENTO - MODIFICADO PARA USAR ROTAS REAIS
const AppRouter = () => {
  const { isInitialized, currentPage } = useCryptoNavigation();
  const { isLoading, isAuthenticated } = useAuth();

  console.log('🚦 AppRouter:', { isInitialized, currentPage, isLoading, isAuthenticated });

  // Aguardar inicialização completa
  if (!isInitialized || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
      }}>
        Carregando aplicação...
      </div>
    );
  }

  // ✅ CORREÇÃO PRINCIPAL: Determinar se deve mostrar login baseado no estado completo
  const shouldShowLogin = currentPage === 'login' || !isAuthenticated;

  console.log('🔍 shouldShowLogin:', shouldShowLogin, {
    currentPage,
    isAuthenticated,
    hasValidToken: TokenUtils.isTokenValid()
  });

  return (
    <Routes>
      {/* ✅ NOVO: Rotas específicas para cada página */}
      <Route
        path="/"
        element={
          shouldShowLogin ? (
            <PageRenderer />
          ) : (
            <Dashboard />
          )
        }
      >
        {!shouldShowLogin && <Route index element={<PageRenderer />} />}
      </Route>

      {/* ✅ NOVO: Rotas específicas */}
      {Object.entries(PAGE_URL_MAPPING).map(([page, url]) => {
        if (url === '/') return null; // Dashboard já está coberto acima
        
        return (
          <Route
            key={page}
            path={url}
            element={
              shouldShowLogin ? (
                <Navigate to="/" replace />
              ) : (
                <Dashboard />
              )
            }
          >
            <Route index element={<PageRenderer />} />
          </Route>
        );
      })}

      {/* ✅ Rota catch-all */}
      <Route
        path="*"
        element={
          !isInitialized || isLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              fontSize: '18px',
            }}>
              Carregando aplicação...
            </div>
          ) : shouldShowLogin ? (
            <Navigate to="/" replace />
          ) : (
            <NotFoundPage />
          )
        }
      />
    </Routes>
  );
};

// Hook personalizado para facilitar o uso
export const useAppNavigation = () => {
  const cryptoNavigation = useCryptoNavigation();
  const { navigateToPage, getParam, getCurrentShareableUrl, forceLogout } = cryptoNavigation;

  // Wrapper simples
  const wrappedNavigateToPage = (page: string, params?: Record<string, any>) => {
    navigateToPage(page, params || {});
  };

  const goToList = (page: string, extraParams?: Record<string, any>) => {
    wrappedNavigateToPage(page, { mode: 'list', ...extraParams });
  };

  const goToNew = (page: string, extraParams?: Record<string, any>) => {
    wrappedNavigateToPage(page, { mode: 'new', ...extraParams });
  };

  const goToEdit = (page: string, id: string, extraParams?: Record<string, any>) => {
    wrappedNavigateToPage(page, { mode: 'edit', id, ...extraParams });
  };

  const goToView = (page: string, id: string, extraParams?: Record<string, any>) => {
    wrappedNavigateToPage(page, { mode: 'view', id, ...extraParams });
  };

  const goToHome = () => {
    wrappedNavigateToPage('dashboard');
  };

  // ✅ NOVO: Função para obter URL atual visível
  const getCurrentVisibleUrl = (): string => {
    const { currentPage } = cryptoNavigation;
    return `${window.location.origin}${URLUtils.pageToUrl(currentPage)}`;
  };

  // Função para copiar URL compartilhável
  const copyCurrentUrl = async (): Promise<boolean> => {
    try {
      const url = getCurrentShareableUrl();
      await navigator.clipboard.writeText(url);
      console.log('📋 URL compartilhável copiada:', url);
      return true;
    } catch (error) {
      console.error('❌ Erro ao copiar URL:', error);
      return false;
    }
  };

  // ✅ NOVO: Função para copiar URL visível
  const copyCurrentVisibleUrl = async (): Promise<boolean> => {
    try {
      const url = getCurrentVisibleUrl();
      await navigator.clipboard.writeText(url);
      console.log('📋 URL visível copiada:', url);
      return true;
    } catch (error) {
      console.error('❌ Erro ao copiar URL visível:', error);
      return false;
    }
  };

  return {
    goToList,
    goToNew,
    goToEdit,
    goToView,
    goToHome,
    getParam,
    navigateToPage: wrappedNavigateToPage,
    getCurrentShareableUrl,
    getCurrentVisibleUrl,
    copyCurrentUrl,
    copyCurrentVisibleUrl,
    forceLogout
  };
};

// Tema "padrão do sistema" — usado quando userTheme é null (o default).
const systemMode = (): AppColorMode =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

// Persiste a escolha do usuário no backend: PUT /user/:id { userTheme } (0=claro, 1=escuro).
const persistUserTheme = (m: AppColorMode) => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (u?.userId) {
      api
        .put(`/user/${u.userId}`, { userTheme: m === "dark" ? 1 : 0 })
        .catch((e) => console.error("Erro ao salvar tema do usuário:", e));
    }
  } catch {
    /* sem usuário logado — só mantém local */
  }
};

// Ao logar / recarregar, resolve o idioma na cascata parceiro → proprietário →
// usuário e aplica. Nada definido em nenhum nível = mantém o que o detector do
// i18next já escolheu.
function LanguageSync() {
  const { user } = useAuth();
  const userId = user?.userId;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    resolverIdioma(userId)
      .then((r) => {
        if (!cancelled) aplicarIdiomaResolvido(r);
      })
      .catch(() => {
        /* falha na resolução → mantém o idioma atual */
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  return null;
}

// Ao logar / recarregar, recupera o tema salvo (GET /user/:id) e aplica.
function UserThemeSync() {
  const { user } = useAuth();
  const { applyUserTheme } = useColorMode();
  const userId = user?.userId;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api
      .get(`/user/${userId}`)
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data?.user ?? resp.data ?? {};
        applyUserTheme(data.userTheme);
      })
      .catch(() => {
        /* falha na busca → mantém o tema atual */
      });
    return () => {
      cancelled = true;
    };
  }, [userId, applyUserTheme]);
  return null;
}

function App() {
  const [mode, setMode] = useState<AppColorMode>(() => {
    const saved = localStorage.getItem(COLOR_MODE_KEY);
    if (saved === "dark") return "dark";
    if (saved === "light") return "light";
    return systemMode(); // nada salvo → padrão do sistema
  });

  const muiTheme = useMemo(() => getTheme(mode), [mode]);

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next: AppColorMode = prev === "light" ? "dark" : "light";
      localStorage.setItem(COLOR_MODE_KEY, next);
      persistUserTheme(next);
      return next;
    });
  }, []);

  // 0 = claro, 1 = escuro, null/ausente = padrão do sistema.
  const applyUserTheme = useCallback((raw: number | null | undefined) => {
    const val = raw == null ? null : Number(raw);
    const next: AppColorMode =
      val === 0 ? "light" : val === 1 ? "dark" : systemMode();
    localStorage.setItem(COLOR_MODE_KEY, next);
    setMode(next);
  }, []);

  const colorMode = useMemo(
    () => ({ mode, toggle, applyUserTheme }),
    [mode, toggle, applyUserTheme]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={muiTheme}>
        {/* enableColorScheme: faz scrollbars e controles nativos seguirem o
            modo claro/escuro (senão o scroll fica branco no tema escuro). */}
        <CssBaseline enableColorScheme />
        <DevAlert />
        <LoadingProvider>
          <AuthProvider>
            <UserThemeSync />
            <LanguageSync />
            <BrowserRouter>
              <CryptoNavigationProvider>
                <AppRouter />
              </CryptoNavigationProvider>
            </BrowserRouter>
          </AuthProvider>
        </LoadingProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;